import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import ConsultantDashboard from '../admin/src/jsx/components/Dashboard/ConsultantDashboard';
import ResumesPage from '../pages/ResumesPage';
import './ModernConsultantLayout.css';
import ConsultantJobDetails from '../components/consultant/ConsultantJobDetails';
import ConsultantApply from '../components/consultant/ConsultantApply';

const ConsultantLayout = () => {
    const navigate = useNavigate();
    const [userName, setUserName] = useState('');
    const [userInitials, setUserInitials] = useState('');
    const [activeTab, setActiveTab] = useState('browse-offers');
    const [sidebarVisible, setSidebarVisible] = useState(true);

    useEffect(() => {
        // Helper: tenter plusieurs clés connues et formats possibles
        const parseFromStorage = (storage) => {
            if (!storage) return null;
            const candidateKeys = [
                'authData',
                'auth',
                'userData',
                'senoffre_auth',
                'loginData',
                'currentUser',
                'user'
            ];
            for (const key of candidateKeys) {
                try {
                    const raw = storage.getItem(key);
                    if (!raw) continue;
                    const data = JSON.parse(raw);
                    // Cas 1: structure fournie par l'utilisateur (token, tokenType, email, user { name, email, ... })
                    if (data && typeof data === 'object') {
                        if (data.user && (data.user.name || data.user.email)) return data.user;
                        // Cas 2: données utilisateur stockées à la racine
                        if (data.name || data.email) return data;
                        // Cas 3: présence d'un token et d'un email à la racine + user imbriqué
                        if (data.token && (data.email || (data.user && (data.user.name || data.user.email)))) {
                            return data.user || { name: data.name, email: data.email };
                        }
                    }
                } catch (e) {
                    // ignore json parse errors
                }
            }
            // Fallback: scanner toutes les clés du localStorage
            try {
                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (!key) continue;
                    const raw = storage.getItem(key);
                    if (!raw) continue;
                    try {
                        const data = JSON.parse(raw);
                        if (data && typeof data === 'object') {
                            if (data.user && (data.user.name || data.user.email)) return data.user;
                            if (data.name || data.email) return data;
                        }
                    } catch (_) {
                        // ignore per-key json parse errors
                    }
                }
            } catch (_) {
                // ignore iteration errors
            }
            return null;
        };

        const getStoredUserPayload = () => {
            // Essayer d'abord localStorage
            const fromLocal = parseFromStorage(window.localStorage);
            if (fromLocal) return fromLocal;
            // Puis sessionStorage
            const fromSession = parseFromStorage(window.sessionStorage);
            if (fromSession) return fromSession;
            return null;
        };

        const updateUserFromStorage = () => {
            const user = getStoredUserPayload();
            const displayName = user?.name || user?.email || 'Consultant';
            setUserName(displayName);
            const initials = (displayName || 'CO')
                .split(/\s+/)
                .filter(Boolean)
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
            setUserInitials(initials || 'CO');
        };

        updateUserFromStorage();

        // Mettre à jour lorsqu'un autre onglet change le storage
        const onStorage = (e) => {
            if (!e) return;
            updateUserFromStorage();
        };
        window.addEventListener('storage', onStorage);
        // Mettre à jour quand la fenêtre reprend le focus
        const onFocus = () => updateUserFromStorage();
        window.addEventListener('focus', onFocus);

        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/';
    };

    return (
        <div className="consultant-layout">
            {/* Modern Navigation Header */}
            <nav className="modern-consultant-navbar">
                <div className="modern-navbar-container">
                    <a className="modern-navbar-brand" href="#">
                        <div className="modern-brand-content">
                            <div className="modern-brand-icon">
                                SO
                            </div>
                            <div className="modern-brand-text">
                                <h1 className="modern-brand-title">SenOffre</h1>
                                <p className="modern-brand-subtitle">Espace Consultant</p>
                            </div>
                        </div>
                    </a>
                    
                    <div className="modern-navbar-actions">
                        {/* User dropdown */}
                        <div className="modern-user-dropdown">
                            <div className="modern-user-info">
                                <div className="modern-user-avatar">
                                    {userInitials}
                                </div>
                                <div className="modern-user-details">
                                    <p className="modern-user-greeting">Bonjour,</p>
                                    <p className="modern-user-name">{userName}</p>
                                </div>
                                <i className="fa fa-chevron-down modern-dropdown-arrow"></i>
                            </div>
                            
                            <div className="modern-dropdown-menu">
                                <div className="dropdown-header">
                                    <i className="fa fa-user-circle me-2"></i>
                                    <strong>Mon Compte</strong>
                                </div>
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); setActiveTab('profile'); }}>
                                    <i className="fa fa-cog me-3"></i>
                                    Paramètres du compte
                                </a>
                                <hr className="dropdown-divider" />
                                <a className="dropdown-item text-danger" href="#" onClick={handleLogout}>
                                    <i className="fa fa-sign-out-alt me-3"></i>
                                    Déconnexion
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Modern Main Content with Sidebar */}
            <div className="modern-content-body">
                {/* Toggle Sidebar Button */}
                <button 
                    className="sidebar-toggle-btn"
                    onClick={() => setSidebarVisible(!sidebarVisible)}
                    aria-label={sidebarVisible ? 'Masquer le menu' : 'Afficher le menu'}
                >
                    <i className={`fa ${sidebarVisible ? 'fa-times' : 'fa-bars'}`}></i>
                </button>

                <div className="modern-layout-wrapper">
                    {/* Detached Sidebar */}
                    <aside className={`modern-sidebar-detached ${sidebarVisible ? 'visible' : 'hidden'}`}>
                        {/* Navigation Menu */}
                        <nav className="sidebar-nav">
                            <div className="nav-section">
                                <h4 className="nav-section-title">Navigation</h4>
                                <button 
                                    className={`nav-item ${activeTab === 'browse-offers' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('browse-offers');
                                        navigate('/consultant/dashboard');
                                    }}
                                >
                                    <div className="nav-item-icon">
                                        <i className="fa fa-briefcase"></i>
                                    </div>
                                    <div className="nav-item-content">
                                        <span className="nav-item-title">Parcourir les offres</span>
                                        <span className="nav-item-subtitle">Toutes les opportunités</span>
                                    </div>
                                </button>

                                <button 
                                    className={`nav-item ${activeTab === 'favorites' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('favorites');
                                        navigate('/consultant/dashboard');
                                    }}
                                >
                                    <div className="nav-item-icon">
                                        <i className="fa fa-heart"></i>
                                    </div>
                                    <div className="nav-item-content">
                                        <span className="nav-item-title">Mes Favoris</span>
                                        <span className="nav-item-subtitle">Offres sauvegardées</span>
                                    </div>
                                </button>

                                <button 
                                    className={`nav-item ${activeTab === 'searches' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('searches');
                                        navigate('/consultant/dashboard');
                                    }}
                                >
                                    <div className="nav-item-icon">
                                        <i className="fa fa-search"></i>
                                    </div>
                                    <div className="nav-item-content">
                                        <span className="nav-item-title">Mes Recherches</span>
                                        <span className="nav-item-subtitle">Alertes et historique</span>
                                    </div>
                                </button>

                                <button 
                                    className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('messages');
                                        navigate('/consultant/dashboard');
                                    }}
                                >
                                    <div className="nav-item-icon">
                                        <i className="fa fa-envelope"></i>
                                    </div>
                                    <div className="nav-item-content">
                                        <span className="nav-item-title">Messages</span>
                                        <span className="nav-item-subtitle">Conversations</span>
                                    </div>
                                </button>

                                <button 
                                    className={`nav-item ${activeTab === 'cvs' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('cvs');
                                        navigate('/consultant/resumes');
                                    }}
                                >
                                    <div className="nav-item-icon">
                                        <i className="fa fa-file-text"></i>
                                    </div>
                                    <div className="nav-item-content">
                                        <span className="nav-item-title">Mes  Liasses</span>
                                        <span className="nav-item-subtitle">Gérer mes liasses</span>
                                    </div>
                                </button>
                            </div>

                            <div className="nav-section">
                                <h4 className="nav-section-title">Mon Compte</h4>
                                <button 
                                    className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('profile');
                                        navigate('/consultant/dashboard');
                                    }}
                                >
                                    <div className="nav-item-icon">
                                        <i className="fa fa-user"></i>
                                    </div>
                                    <div className="nav-item-content">
                                        <span className="nav-item-title">Mon Profil</span>
                                        <span className="nav-item-subtitle">Paramètres</span>
                                    </div>
                                </button>
                            </div>
                        </nav>
                    </aside>

                    {/* Main Content Area */}
                    <main className={`modern-main-content ${sidebarVisible ? 'with-sidebar' : 'full-width'}`}>
                        <Routes>
                            <Route path="/dashboard" element={<ConsultantDashboard activeTab={activeTab} setActiveTab={setActiveTab} />} />
                            <Route path="/resumes" element={<ResumesPage />} />
                            <Route path="/job-detail" element={<ConsultantJobDetails />} />
                            <Route path="/job-apply" element={<ConsultantApply />} />
                            <Route path="/*" element={<ConsultantDashboard activeTab={activeTab} setActiveTab={setActiveTab} />} />
                        </Routes>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default ConsultantLayout;
